import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {AsyncState, RejectedState} from '../../models/async_state';
import {Status} from '../../enums/status';
import {CustomerPreferencesInput, GetMeQuery} from '../../gql/customer/graphql';
import {Customer} from '../../network/customer';

interface State extends GetMeQuery {}

export const fetchMe = createAsyncThunk('customerInfo/fetchMe', async (data: {token: string}) => {
    const result = await Customer.me(data.token);
    return result.data;
});

export const sendProfileData = createAsyncThunk(
    'customerInfo/sendProfileData',
    async (data: {token: string; data: Array<{key: string; value: string}>}) => {
        const result = await Customer.sendProfileData(data.token, data.data);
        return result.data;
    },
);
export const sendPreferenceData = createAsyncThunk(
    'customerInfo/sendPreferenceData',
    async (data: {token: string; preferenceData: CustomerPreferencesInput}) => {
        const result = await Customer.sendPreferencesData(data.token, data.preferenceData);
        return result.data;
    },
);

export const sendDefaultLocation = createAsyncThunk(
    'customerInfo/sendDefaultLocation',
    async (data: {token: string; locationId: number}) => {
        const result = await Customer.sendDefaultLocation(data.token, data.locationId);
        return result.data;
    },
);

export const sendProfilePicture = createAsyncThunk(
    'customerInfo/sendProfilePicture',
    async (data: {token: string; file: File}) => {
        const result = await Customer.sendProfilePicture(data.token, data.file);
        return result.data;
    },
);

export const removeAccount = createAsyncThunk('customerInfo/removeAccount', async (data: {token: string}) => {
    const result = await Customer.deleteAccount(data.token);
    return result.data;
});

export const markOnboardingComplete = createAsyncThunk(
    'customerInfo/markOnboardingComplete',
    async (data: {token: string}) => {
        const result = await Customer.markOnboardingCompleted(data.token);
        return result.data;
    },
);
export const addVehicle = createAsyncThunk(
    'customerInfo/addVehicle',
    async (data: {token: string; license_plate: string}) => {
        const result = await Customer.addVehicle(data.license_plate, data.token);
        return result.data;
    },
);

export const updateVehicle = createAsyncThunk(
    'customerInfo/updateVehicle',
    async (data: {token: string; tires: string; lease: boolean; mileage: number; id: number}) => {
        const result = await Customer.editVehicle(data.id, data.tires, data.lease, data.mileage, data.token);
        return result.data;
    },
);
export const deleteVehicle = createAsyncThunk(
    'customerInfo/deleteVehicle',
    async (data: {token: string; id: number}) => {
        const result = await Customer.deleteVehicle(data.id, data.token);
        return result.data;
    },
);

export const customerInfoSlice = createSlice({
    name: 'customerInfo',
    initialState: () => {
        const initialState: AsyncState<State> = {
            status: Status.IDLE,
        };
        return initialState as AsyncState<State>;
    },
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchMe.pending, (state) => {
            state.status = Status.PENDING;
        });
        builder.addCase(fetchMe.fulfilled, (state, action) => {
            state.status = Status.FULFILLED;
            state.me = action.payload.me;
            state.customerFields = action.payload.customerFields;
            state.__typename = action.payload.__typename;
        });
        builder.addCase(fetchMe.rejected, (state, action) => {
            state.status = Status.REJECTED;
            (state as RejectedState<State>).error = action.error.message ?? 'Error';
        });

        builder.addCase(sendProfileData.pending, (state) => {
            state.status = Status.PENDING;
        });
        builder.addCase(sendProfileData.fulfilled, (state, action) => {
            state.status = Status.FULFILLED;
            state.me = {
                __typename: 'CustomerAccount',
                ...state.me,
                customer: action.payload?.customer,
            };
        });

        builder.addCase(sendProfileData.rejected, (state, action) => {
            state.status = Status.REJECTED;
            (state as RejectedState<State>).error = action.error.message ?? 'Error';
        });

        builder.addCase(sendDefaultLocation.pending, (state) => {
            state.status = Status.PENDING;
        });
        builder.addCase(sendDefaultLocation.fulfilled, (state, action) => {
            state.status = Status.FULFILLED;
            if (state.me?.customer) {
                state.me.customer.default_location_id = action.payload?.SetDefaultLocation;
            }
        });
        builder.addCase(sendDefaultLocation.rejected, (state, action) => {
            state.status = Status.REJECTED;
            (state as RejectedState<State>).error = action.error.message ?? 'Error';
        });

        builder.addCase(sendPreferenceData.pending, (state) => {
            state.status = Status.PENDING;
        });
        builder.addCase(sendPreferenceData.fulfilled, (state, action) => {
            state.status = Status.FULFILLED;
            if (state.me?.customer) {
                state.me.customer.preferences = action.payload?.customer;
            }
        });
        builder.addCase(sendPreferenceData.rejected, (state, action) => {
            state.status = Status.REJECTED;
            (state as RejectedState<State>).error = action.error.message ?? 'Error';
        });

        builder.addCase(sendProfilePicture.pending, (state) => {
            state.status = Status.PENDING;
        });
        builder.addCase(sendProfilePicture.fulfilled, (state, action) => {
            state.status = Status.FULFILLED;
            if (state.me?.customer) {
                state.me = {
                    ...state.me,
                    customer: {
                        ...state.me.customer,
                        profilePicture: action.payload?.me?.customer?.profilePicture,
                    },
                };
            }
        });
        builder.addCase(sendProfilePicture.rejected, (state, action) => {
            state.status = Status.REJECTED;
            (state as RejectedState<State>).error = action.error.message ?? 'Error';
        });

        builder.addCase(removeAccount.pending, (state) => {
            state.status = Status.PENDING;
        });
        builder.addCase(removeAccount.fulfilled, () => {
            return {
                status: Status.IDLE,
            };
        });
        builder.addCase(removeAccount.rejected, (state, action) => {
            state.status = Status.REJECTED;
            (state as RejectedState<State>).error = action.error.message ?? 'Error';
        });
        builder.addCase(addVehicle.pending, (state) => {
            state.status = Status.PENDING;
        });
        builder.addCase(addVehicle.fulfilled, (state, action) => {
            state.status = Status.FULFILLED;
            const vehicle = action.payload?.AddVehicle;
            if (state.me?.customer?.vehicles && vehicle) {
                state.me.customer.vehicles.push(vehicle);
            }
        });
        builder.addCase(addVehicle.rejected, (state, action) => {
            state.status = Status.REJECTED;
            (state as RejectedState<State>).error = action.error.message ?? 'Error';
        });
        builder.addCase(deleteVehicle.pending, (state) => {
            state.status = Status.PENDING;
        });
        builder.addCase(deleteVehicle.fulfilled, (state, action) => {
            state.status = Status.FULFILLED;
            if (state.me?.customer?.vehicles) {
                state.me.customer.vehicles = state.me.customer?.vehicles?.filter(
                    (vehicle) => vehicle?.id !== action.meta.arg.id,
                );
            }
        });
        builder.addCase(deleteVehicle.rejected, (state, action) => {
            state.status = Status.REJECTED;
            (state as RejectedState<State>).error = action.error.message ?? 'Error';
        });
    },
});
